import { createSlice } from "@reduxjs/toolkit";

import { SelectOptions } from "components/Common/Select/index";

import { RoleTypesSelectValues } from "hooks/pages/useRolesAndPermissions";

import {
  createRole,
  deleteRole,
  getAllRoles,
  getMeetingRoles,
  updateRole,
} from "./actions";
import { Role } from "./types";

export type RolesState = {
  error: any;
  loading: boolean;
  roles: Role[];
  meetingRoles: SelectOptions[];
  rolesPage: {
    openedGroups: string[];
    roleTypeView: SelectOptions;
    roleToEditOrDelete: Role | null;
    isAddNewRoleView: boolean;
    isDescriptionModalOpen: boolean;
    isDeleteModalOpen: boolean;
    searchValue: string;
  };
};

const initialState: RolesState = {
  error: false,
  loading: false,
  roles: [],
  meetingRoles: [],
  rolesPage: {
    openedGroups: [],
    roleTypeView: {
      value: RoleTypesSelectValues.GLOBAL,
      label: "general",
    },
    roleToEditOrDelete: null,
    isAddNewRoleView: false,
    isDescriptionModalOpen: false,
    isDeleteModalOpen: false,
    searchValue: "",
  },
};

export const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    setRoleTypeView: (state, action) => {
      state.rolesPage.roleTypeView = action.payload;
    },
    setRoleToEditOrDelete: (state, action) => {
      state.rolesPage.roleToEditOrDelete = action.payload;
    },
    setIsAddNewRoleView: (state, action) => {
      state.rolesPage.isAddNewRoleView = action.payload;
    },
    setOpenedGroups: (state, action) => {
      state.rolesPage.openedGroups = action.payload;
    },
    setIsDescriptionModalOpen: (state, action) => {
      state.rolesPage.isDescriptionModalOpen = action.payload;
    },
    setIsDeleteModalOpen: (state, action) => {
      state.rolesPage.isDeleteModalOpen = action.payload;
    },
    setSearchValue: (state, action) => {
      state.rolesPage.searchValue = action.payload;
    },
    resetAll: (state) => {
      if (initialState !== state) {
        state.rolesPage = initialState.rolesPage;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllRoles.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getAllRoles.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getAllRoles.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.roles = action.payload.data;
    });
    builder.addCase(updateRole.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateRole.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(updateRole.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.roles = state.roles.map((role) => {
        if (role.id === action.payload.data.id) {
          return action.payload.data;
        }
        return role;
      });
    });
    builder.addCase(createRole.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createRole.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(createRole.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.roles = [...state.roles, action.payload.data];
    });
    builder.addCase(deleteRole.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(deleteRole.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteRole.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.roles = state.roles.filter(
        (role) => role.name !== action.payload.roleName
      );
    });
    builder.addCase(getMeetingRoles.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getMeetingRoles.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getMeetingRoles.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meetingRoles = action.payload.data;
    });
  },
});

export const {
  setRoleTypeView,
  setRoleToEditOrDelete,
  setIsAddNewRoleView,
  setOpenedGroups,
  setIsDescriptionModalOpen,
  setIsDeleteModalOpen,
  setSearchValue,
  resetAll,
} = rolesSlice.actions;
